<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxes: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:show', 'created']);

const { t } = useI18n();

const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
// Meta requires an example value per {{n}} placeholder before it will accept a
// template, so the form collects them rather than letting the submission bounce.
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

const inboxId = ref(props.inboxes[0]?.id ?? '');
const name = ref('');
const category = ref('UTILITY');
const language = ref('en_US');
const headerText = ref('');
const bodyText = ref('');
const footerText = ref('');
const buttons = ref([]);
const examples = ref([]);
const isSubmitting = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const highestVariable = text => {
  const indexes = [...String(text).matchAll(VARIABLE_PATTERN)].map(match =>
    Number(match[1])
  );
  return indexes.length ? Math.max(...indexes) : 0;
};

const variableCount = computed(() => highestVariable(bodyText.value));
const headerHasVariable = computed(() => highestVariable(headerText.value) > 0);
const headerExample = ref('');

watch(variableCount, count => {
  const next = examples.value.slice(0, count);
  while (next.length < count) next.push('');
  examples.value = next;
});

const nameError = computed(() => {
  if (!name.value.trim()) return t('WHATSAPP_TEMPLATE_BUILDER.NAME_REQUIRED');
  if (!/^[a-z0-9_]+$/.test(name.value.trim().toLowerCase()))
    return t('WHATSAPP_TEMPLATE_BUILDER.NAME_INVALID');
  return '';
});

const error = computed(() => {
  if (!inboxId.value) return t('WHATSAPP_TEMPLATE_BUILDER.INBOX_REQUIRED');
  if (nameError.value) return nameError.value;
  if (!bodyText.value.trim())
    return t('WHATSAPP_TEMPLATE_BUILDER.BODY_REQUIRED');
  if (examples.value.some(example => !example.trim()))
    return t('WHATSAPP_TEMPLATE_BUILDER.EXAMPLES_REQUIRED');
  if (headerHasVariable.value && !headerExample.value.trim())
    return t('WHATSAPP_TEMPLATE_BUILDER.EXAMPLES_REQUIRED');
  if (
    buttons.value.some(
      button => button.type === 'URL' && !String(button.url || '').trim()
    )
  )
    return t('WHATSAPP_TEMPLATE_BUILDER.URL_REQUIRED');
  if (
    buttons.value.some(
      button =>
        button.type === 'PHONE_NUMBER' &&
        !String(button.phone_number || '').trim()
    )
  )
    return t('WHATSAPP_TEMPLATE_BUILDER.PHONE_REQUIRED');
  return '';
});

const addButton = () => {
  buttons.value = [
    ...buttons.value,
    { type: 'QUICK_REPLY', text: '', url: '', phone_number: '' },
  ];
};

const removeButton = index => {
  buttons.value = buttons.value.filter((_, i) => i !== index);
};

const reset = () => {
  name.value = '';
  category.value = 'UTILITY';
  language.value = 'en_US';
  headerText.value = '';
  bodyText.value = '';
  footerText.value = '';
  buttons.value = [];
  examples.value = [];
  headerExample.value = '';
};

const buildPayload = () => {
  const payload = {
    name: name.value.trim().toLowerCase(),
    category: category.value,
    language: language.value.trim(),
    body: { text: bodyText.value.trim() },
  };
  if (examples.value.length) payload.body.examples = examples.value;
  if (headerText.value.trim()) {
    payload.header = { format: 'TEXT', text: headerText.value.trim() };
    if (headerHasVariable.value) payload.header.example = headerExample.value;
  }
  if (footerText.value.trim())
    payload.footer = { text: footerText.value.trim() };
  if (buttons.value.length) {
    payload.buttons = buttons.value
      .filter(button => String(button.text || '').trim())
      .map(button => {
        const built = { type: button.type, text: button.text.trim() };
        if (button.type === 'URL') built.url = button.url.trim();
        if (button.type === 'PHONE_NUMBER')
          built.phone_number = button.phone_number.trim();
        return built;
      });
  }
  return payload;
};

const submit = async () => {
  if (error.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await InboxesAPI.createMessageTemplate(inboxId.value, buildPayload());
    useAlert(t('WHATSAPP_TEMPLATE_BUILDER.SUCCESS'));
    reset();
    localShow.value = false;
    emit('created');
  } catch (submitError) {
    useAlert(
      submitError?.response?.data?.error || t('WHATSAPP_TEMPLATE_BUILDER.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  isVisible => {
    if (!isVisible) reset();
    else if (!inboxId.value) inboxId.value = props.inboxes[0]?.id ?? '';
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" size="modal-big">
    <woot-modal-header
      :header-title="t('WHATSAPP_TEMPLATE_BUILDER.TITLE')"
      :header-content="t('WHATSAPP_TEMPLATE_BUILDER.SUBTITLE')"
    />
    <div class="flex flex-col gap-5 px-8 py-6">
      <div class="flex gap-3">
        <div class="flex flex-col flex-1 gap-1">
          <label class="text-sm font-medium text-n-slate-12" for="tb-inbox">
            {{ t('WHATSAPP_TEMPLATE_BUILDER.INBOX') }}
          </label>
          <select
            id="tb-inbox"
            v-model="inboxId"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col flex-1 gap-1">
          <label class="text-sm font-medium text-n-slate-12" for="tb-category">
            {{ t('WHATSAPP_TEMPLATE_BUILDER.CATEGORY') }}
          </label>
          <select
            id="tb-category"
            v-model="category"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option v-for="option in CATEGORIES" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>
      </div>

      <div class="flex gap-3">
        <Input
          v-model="name"
          class="flex-1"
          :label="t('WHATSAPP_TEMPLATE_BUILDER.NAME')"
          placeholder="order_update"
          :message="nameError"
          :message-type="nameError ? 'error' : 'info'"
        />
        <Input
          v-model="language"
          class="flex-1"
          :label="t('WHATSAPP_TEMPLATE_BUILDER.LANGUAGE')"
          placeholder="en_US"
        />
      </div>

      <Input
        v-model="headerText"
        :label="t('WHATSAPP_TEMPLATE_BUILDER.HEADER')"
        :placeholder="t('WHATSAPP_TEMPLATE_BUILDER.HEADER_PLACEHOLDER')"
      />
      <Input
        v-if="headerHasVariable"
        v-model="headerExample"
        :label="t('WHATSAPP_TEMPLATE_BUILDER.HEADER_EXAMPLE')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12" for="tb-body">
          {{ t('WHATSAPP_TEMPLATE_BUILDER.BODY') }}
        </label>
        <textarea
          id="tb-body"
          v-model="bodyText"
          rows="4"
          :placeholder="t('WHATSAPP_TEMPLATE_BUILDER.BODY_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm rounded-lg resize-y bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATE_BUILDER.BODY_HINT') }}
        </span>
      </div>

      <div v-if="examples.length" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATE_BUILDER.EXAMPLES') }}
        </label>
        <Input
          v-for="(example, index) in examples"
          :key="index"
          :model-value="example"
          size="sm"
          :placeholder="`{{${index + 1}}}`"
          @update:model-value="value => (examples[index] = value)"
        />
      </div>

      <Input
        v-model="footerText"
        :label="t('WHATSAPP_TEMPLATE_BUILDER.FOOTER')"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATE_BUILDER.BUTTONS') }}
        </label>
        <div
          v-for="(button, index) in buttons"
          :key="index"
          class="flex gap-2 items-end"
        >
          <select
            v-model="button.type"
            class="px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option v-for="type in BUTTON_TYPES" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
          <Input
            v-model="button.text"
            class="flex-1"
            size="sm"
            :placeholder="t('WHATSAPP_TEMPLATE_BUILDER.BUTTON_TEXT')"
          />
          <Input
            v-if="button.type === 'URL'"
            v-model="button.url"
            class="flex-1"
            size="sm"
            placeholder="https://example.com"
          />
          <Input
            v-if="button.type === 'PHONE_NUMBER'"
            v-model="button.phone_number"
            class="flex-1"
            size="sm"
            placeholder="+27000000000"
          />
          <Button
            variant="ghost"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            @click="removeButton(index)"
          />
        </div>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          class="self-start"
          :label="t('WHATSAPP_TEMPLATE_BUILDER.ADD_BUTTON')"
          @click="addButton"
        />
      </div>

      <div class="flex items-center justify-end gap-3 pt-2">
        <span v-if="error" class="mr-auto text-xs text-n-ruby-9">
          {{ error }}
        </span>
        <Button
          variant="faded"
          color="slate"
          :label="t('WHATSAPP_TEMPLATE_BUILDER.CANCEL')"
          @click="localShow = false"
        />
        <Button
          color="blue"
          :disabled="!!error"
          :is-loading="isSubmitting"
          :label="t('WHATSAPP_TEMPLATE_BUILDER.SUBMIT')"
          @click="submit"
        />
      </div>
    </div>
  </woot-modal>
</template>
